import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import useBreakpointLogger from '../../../utils/breakpointLogger';

const CustomLegends = () => {
  const legends = [
    {
      label: (
        <Tooltip placement="top" title="Målt forbrug under aktiv styring.">
          Aktiv styring
        </Tooltip>
      ),
      color: 'hsl(63, 100%, 40%)',
    }, // Custom Label 1
    {
      label: (
        <Tooltip
          placement="top"
          title="Målt baseline dags forbrug uden aktiv styring."
        >
          Baseline
        </Tooltip>
      ),
      color: 'hsl(10, 85%, 47%)',
    }, // Custom Label 2
    {
      label: (
        <Tooltip
          placement="top"
          title="Målt dags forbrug med inaktiv styring - forbrug er ikke underlagt aktiv styring, eller medregnet som en baseline dag i den samlede besparelse."
        >
          Inaktiv Styring
        </Tooltip>
      ),
      color: 'lightgray',
    }, // Custom Label 2
  ];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
      {legends.map((legend, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: legend.color,
              borderRadius: '75%',
              marginRight: 1,
            }}
          />
          <Typography variant="body2">{legend.label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

// Helper function to find the first midday timestamp
const findFirstMiddayAnchor = (data) => {
  for (const point of data) {
    // Convert UTC timestamp to Europe/Copenhagen timezone
    const localTime = DateTime.fromISO(point.Timestamp, { zone: 'utc' });

    // Check if the timestamp is at local noon in Europe/Copenhagen
    if (localTime.hour === 12 && localTime.minute === 0) {
      return DateTime.fromISO(point.Timestamp, { zone: 'utc' }); // Return UTC timestamp matching local noon
    }
  }
  return null; // No valid local noon timestamp found
};

const isWithinPeriod = (date, period) => {
  // Convert period start and end to UTC DateTime objects
  const startDate = DateTime.fromISO(period.Start, { zone: 'utc' });
  const endDate = DateTime.fromISO(period.End, { zone: 'utc' });

  // Check if the date falls within this 24-hour baseline period
  const withinPeriod = date >= startDate && date < endDate;

  return withinPeriod;
};

const formatRangeLabel = (dateTime) => {
  return dateTime.toFormat('d. MMM'); // Format to '1. Jun.'
};

const determineColor = (date, controlPeriods = [], baselineDays) => {
  // Convert the date to UTC to match the baseline period format
  const formattedDate = DateTime.fromISO(date, { zone: 'utc' });

  // Wrap baselineDays in an array if it’s a single object
  const baselinePeriods = Array.isArray(baselineDays)
    ? baselineDays
    : [baselineDays];

  // Check if the date falls within any baseline period
  for (const period of baselinePeriods) {
    if (isWithinPeriod(formattedDate, period)) {
      return 'hsl(10, 85%, 47%)'; // Red color for baseline
    }
  }

  // Check if the date falls within any control period
  for (const period of controlPeriods) {
    if (isWithinPeriod(formattedDate, period)) {
      return 'hsl(63, 100%, 40%)'; // Green color for control
    }
  }

  return 'lightgray'; // Default color for other days
};

const partitionConsumptionData = async (
  data,
  baselineDays = [],
  controlPeriods = [],
) => {
  if (!data || data.length === 0) return [];

  const aggregatedData = [];
  let currentSum = 0;

  const getPeriodStart = (timestamp) => {
    return DateTime.fromISO(timestamp, { zone: 'utc' }).set({
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
  };

  const sortedData = [...data].sort(
    (a, b) => DateTime.fromISO(a.Timestamp) - DateTime.fromISO(b.Timestamp),
  );
  let currentPeriodStart = getPeriodStart(sortedData[0]?.Timestamp);

  for (const point of sortedData) {
    const pointDate = DateTime.fromISO(point.Timestamp, { zone: 'utc' });

    // Fill in gaps with zero values for any missing days
    while (pointDate > currentPeriodStart.plus({ days: 1 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum,
        Base: 'kWh',
        color: color,
      });
      currentPeriodStart = currentPeriodStart.plus({ days: 1 });
      currentSum = 0; // Reset for the next period
    }

    if (pointDate >= currentPeriodStart.plus({ days: 1 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum,
        Base: 'kWh',
        color: color,
      });
      currentPeriodStart = currentPeriodStart.plus({ days: 1 });
      currentSum = 0;
    }

    currentSum += typeof point.Value === 'number' ? point.Value : 0;
  }

  if (currentPeriodStart) {
    const color = determineColor(
      currentPeriodStart.toISO(),
      controlPeriods,
      baselineDays,
    );
    aggregatedData.push({
      label: formatRangeLabel(currentPeriodStart),
      value: currentSum,
      Base: 'kWh',
      color: color,
    });
  }

  return aggregatedData;
};

// Partition Accumulated Data Asynchronously with refined anchor start
const partitionAccumulatedDataAsync = async (
  data,
  baselineDays = [],
  controlPeriods = [],
) => {
  if (!data || data.length === 0) return [];

  const consumptionData = [];
  for (let i = 1; i < data.length; i++) {
    const currentPoint = data[i];
    const previousPoint = data[i - 1];
    const timeGap = DateTime.fromISO(currentPoint.Timestamp, {
      zone: 'utc',
    }).diff(
      DateTime.fromISO(previousPoint.Timestamp, { zone: 'utc' }),
      'days',
    ).days;

    if (timeGap > 1) {
      // Skip this point as it's after a long gap
      continue;
    }

    const consumptionValue = currentPoint.Value - previousPoint.Value;
    if (consumptionValue >= 0) {
      consumptionData.push({
        Timestamp: currentPoint.Timestamp,
        Value: consumptionValue,
        Base: currentPoint.Base,
      });
    }
  }

  // Find the initial start period based on the first midday timestamp
  const firstMiddayTimestamp = findFirstMiddayAnchor(consumptionData);
  if (!firstMiddayTimestamp) return []; // Return if no midday anchor is found

  // Set the period start to the identified midday timestamp
  let currentPeriodStart = firstMiddayTimestamp;
  const aggregatedData = [];
  let currentSum = 0;

  // Aggregate from the first midday timestamp onward
  for (const point of consumptionData) {
    const pointDate = DateTime.fromISO(point.Timestamp, { zone: 'utc' });

    while (pointDate > currentPeriodStart.plus({ days: 1 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum,
        Base: point.Base,
        color: color,
      });
      currentPeriodStart = currentPeriodStart.plus({ days: 1 });
      currentSum = 0; // Reset for the next period
    }

    if (pointDate >= currentPeriodStart.plus({ hours: 24 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum,
        Base: point.Base,
        color: color,
      });

      currentPeriodStart = currentPeriodStart.plus({ hours: 24 });
      currentSum = 0;
    }

    currentSum += typeof point.Value === 'number' ? point.Value : 0;
  }

  // Finalize the last period's data
  if (currentPeriodStart) {
    const color = determineColor(
      currentPeriodStart.toISO(),
      controlPeriods,
      baselineDays,
    );
    aggregatedData.push({
      label: formatRangeLabel(currentPeriodStart),
      value: currentSum,
      Base: 'kWh',
      color: color,
    });
  }

  return aggregatedData;
};

const partitionPowerData = async (
  data,
  baselineDays = [],
  controlPeriods = [],
) => {
  if (!data || data.length === 0) return [];

  const aggregatedData = [];
  let currentSum = 0;
  let currentPeriodStart = null;

  // Find the first midday anchor similar to how partitionAccumulatedData does
  const firstMiddayAnchor = findFirstMiddayAnchor(data);
  if (!firstMiddayAnchor) return []; // Return if no valid midday anchor is found

  // Set the current period start to the first midday timestamp
  currentPeriodStart = firstMiddayAnchor;

  // Convert the data values from W to kWh
  for (const point of data) {
    const pointDate = DateTime.fromISO(point.Timestamp, { zone: 'utc' });

    while (pointDate > currentPeriodStart.plus({ days: 1 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum,
        Base: 'kWh',
        color: color,
      });
      currentPeriodStart = currentPeriodStart.plus({ days: 1 });
      currentSum = 0; // Reset for the next period
    }

    // If the point's date is outside the current 24-hour period, finalize the current period
    if (pointDate >= currentPeriodStart.plus({ days: 1 })) {
      const color = determineColor(
        currentPeriodStart.toISO(),
        controlPeriods,
        baselineDays,
      );
      aggregatedData.push({
        label: formatRangeLabel(currentPeriodStart),
        value: currentSum / 1000, // Convert to kWh (Watts to kilowatt-hours)
        Base: 'kWh',
        color: color,
      });

      // Move to the next 24-hour period
      currentPeriodStart = currentPeriodStart.plus({ days: 1 });
      currentSum = 0; // Reset the sum for the new period
    }

    // Accumulate the power value in W, later converted to kWh
    currentSum += typeof point.Value === 'number' ? point.Value : 0;
  }

  // Finalize the last period if needed
  if (currentPeriodStart) {
    const color = determineColor(
      currentPeriodStart.toISO(),
      controlPeriods,
      baselineDays,
    );
    aggregatedData.push({
      label: formatRangeLabel(currentPeriodStart),
      value: currentSum / 1000, // Convert to kWh
      Base: 'kWh',
      color: color,
    });
  }

  return aggregatedData;
};

const EnergyConsumptionChart = ({
  data,
  loading,
  loadingMessage,
  seasonStart,
  seasonEnd,
  controlPeriods,
  baselineDays,
}) => {
  const [chartData, setChartData] = useState([]);
  const [visibleRange, setVisibleRange] = useState([0, 500]); // Control visible range
  const cacheRef = useRef(new Map()); // Create a cache to store processed data
  const [showAll, setShowAll] = useState(false); // State to track if showing all data
  useBreakpointLogger();

  const seasonStartDate = DateTime.fromMillis(seasonStart)
    .setLocale('da')
    .toFormat('d. MMM yyyy');
  const seasonEndDate = DateTime.fromMillis(seasonEnd)
    .setLocale('da')
    .toFormat('d. MMM yyyy');
  const currentDate = DateTime.now().setLocale('da').toFormat('d. MMM yyyy');

  const processData = async () => {
    if (!data || data.length === 0) return;

    // const cacheKey = `${data[0]?.UnitId}-${data[0]?.DataType}-${data[0]?.Timestamp}`;
    // if (cacheRef.current.has(cacheKey)) {
    //   setChartData(cacheRef.current.get(cacheKey));
    //   return;
    // }

    const dataType = data[0]?.DataType;
    let aggregated = [];

    if (
      dataType === 'accumulated-heat' ||
      dataType === 'accumulated-power' ||
      dataType === 'accumulated-flow'
    ) {
      aggregated = await partitionAccumulatedDataAsync(
        data,
        baselineDays,
        controlPeriods,
      );
    } else if (
      dataType === 'heat-consumption' ||
      dataType === 'power-consumption'
    ) {
      aggregated = await partitionConsumptionData(
        data,
        baselineDays,
        controlPeriods,
      );
    } else if (dataType === 'power') {
      aggregated = await partitionPowerData(data, baselineDays, controlPeriods);
    } else {
      console.error(`Unsupported data type: ${dataType}`);
    }

    // cacheRef.current.set(cacheKey, aggregated);
    setChartData(aggregated);
  };

  useEffect(() => {
    processData();
  }, [data, baselineDays, controlPeriods, determineColor]);

  const visibleChartData = useMemo(() => {
    // Return only the data points within the visible range for rendering
    return chartData.slice(visibleRange[0], visibleRange[1]);
  }, [chartData, visibleRange]);

  const chartConfig = useMemo(() => {
    if (!visibleChartData || visibleChartData.length === 0) return null;

    const barColors = visibleChartData.map((item) => item.color);

    return {
      xAxis: [
        {
          data: visibleChartData.map((item) => item.label) || [],
          scaleType: 'band',
          tickFormat: (index) => visibleChartData[index]?.label,
          tickPlacement: 'middle',
          valueFormatter: (month, context) =>
            context.location === 'tick'
              ? month // Shows the month name on the x-axis
              : `Forbruget aflæses over 24 i tidsrummet 12:00 - 12:00`, // Custom text in the tooltip
          colorMap: {
            type: 'ordinal',
            values: visibleChartData.map((item) => item.label),
            colors: barColors,
            unknownColor: 'blue', // Default color if something goes wrong
          },
        },
      ],
      series: [
        {
          data: visibleChartData.map((item) => Math.round(item.value)),
          label: `Aflæst forbrug (${visibleChartData[0]?.Base || ''})`,
          color: 'transparent', // Default color, will be overridden by colorMap
          tooltip: visibleChartData.map(
            (item) => `${item.label}: ${Math.round(item.value)} ${item.Base}`,
          ),
        },
      ],
    };
  }, [visibleChartData, baselineDays, controlPeriods]);

  if (!chartData || chartData.length === 0) {
    return;
  }

  return (
    <>
      {loading ? (
        <Skeleton variant="rounded" height={400} />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mt: 2,
          }}
        >
          <CustomLegends /> {/* Render the custom legends here */}
          <BarChart
            borderRadius={1}
            xAxis={chartConfig.xAxis}
            // skipAnimation={true}
            series={chartConfig.series}
            height={400} // Adjust as needed
            margin={{ top: 70, bottom: 30 }}
          />
          <Box
            gap={2}
            sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" color="text.primary">
                {seasonStartDate} - {currentDate}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Varmesæsonen starter {seasonStartDate} og slutter{' '}
                {seasonEndDate}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default EnergyConsumptionChart;
